/**
 * Access-token storage for the Admin API session.
 *
 * The token is the Supabase access token that `POST /api/v1/admin/auth/login`
 * returns; the FastAPI Admin API verifies it against the Supabase JWKS on every
 * protected request.
 *
 * It is kept in two places on purpose:
 *   - localStorage, which the API client reads on each request;
 *   - a plain cookie, which `src/middleware.ts` reads to gate routes on the
 *     server before a page is rendered.
 *
 * The cookie is deliberately not HttpOnly: the browser has to attach the token
 * to cross-origin calls itself, so a JS-invisible cookie would buy nothing here.
 */

export const TOKEN_COOKIE = 'pm_access_token';
const TOKEN_KEY = 'pm_access_token';
const USER_KEY = 'pm_user';
/**
 * The refresh token and the access token's expiry, both returned by
 * `POST /auth/login` and both previously thrown away.
 *
 * They are the two things a session needs to outlive its first hour, and
 * `lib/auth/refresh.ts` is what spends them. Kept in localStorage only — the
 * middleware gates on the access-token cookie and has no use for either, and a
 * refresh token in a non-HttpOnly cookie would be sent on every request to the
 * origin for no benefit.
 */
const REFRESH_KEY = 'pm_refresh_token';
const EXPIRES_AT_KEY = 'pm_expires_at';

export interface StoredUser {
  id: string;
  email: string | null;
  full_name: string | null;
}

const isBrowser = () => typeof window !== 'undefined';

function writeCookie(name: string, value: string, maxAgeSeconds: number): void {
  if (typeof document === 'undefined') return;
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAgeSeconds}; SameSite=Lax${secure}`;
}

function clearCookie(name: string): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax`;
}

export function saveSession(
  token: string,
  expiresInSeconds: number,
  user?: StoredUser,
  refreshToken?: string | null
): void {
  if (!isBrowser()) return;
  try {
    localStorage.setItem(TOKEN_KEY, token);
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
    // Absent on a renewal that did not rotate the token: keep the one we have
    // rather than clearing it, or the session ends at the following refresh.
    if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
    const lifetime = Math.min(Math.max(expiresInSeconds || 3600, 60), 60 * 60 * 24);
    localStorage.setItem(EXPIRES_AT_KEY, String(Date.now() + lifetime * 1000));
  } catch {
    // Private-mode / storage-disabled browsers still get the cookie below.
  }
  // Clamp to something sane so a bad expires_in cannot produce a session cookie
  // that dies on navigation or one that outlives the token by days.
  const maxAge = Math.min(Math.max(expiresInSeconds || 3600, 60), 60 * 60 * 24);
  writeCookie(TOKEN_COOKIE, token, maxAge);
}

export function getToken(): string | null {
  if (!isBrowser()) return null;
  try {
    const fromStorage = localStorage.getItem(TOKEN_KEY);
    if (fromStorage) return fromStorage;
  } catch {
    /* fall through to the cookie */
  }
  const match = document.cookie.match(new RegExp(`(?:^|; )${TOKEN_COOKIE}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

export function getStoredUser(): StoredUser | null {
  if (!isBrowser()) return null;
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as StoredUser) : null;
  } catch {
    return null;
  }
}

/** The Supabase refresh token, when the login response carried one. */
export function getRefreshToken(): string | null {
  if (!isBrowser()) return null;
  try {
    return localStorage.getItem(REFRESH_KEY);
  } catch {
    return null;
  }
}

/**
 * Seconds until the access token expires, or null when unknown.
 *
 * Negative once it has expired, which the scheduler treats as "refresh now"
 * rather than clamping to zero — a token that died while the tab was asleep is
 * exactly the case renewal exists for.
 */
export function secondsUntilExpiry(): number | null {
  if (!isBrowser()) return null;
  try {
    const raw = localStorage.getItem(EXPIRES_AT_KEY);
    if (!raw) return null;
    const at = Number(raw);
    if (!Number.isFinite(at)) return null;
    return Math.round((at - Date.now()) / 1000);
  } catch {
    return null;
  }
}

export function clearSession(): void {
  if (!isBrowser()) return;
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(EXPIRES_AT_KEY);
  } catch {
    /* nothing to clean up */
  }
  clearCookie(TOKEN_COOKIE);
}
